import React, { FC, useEffect, useMemo, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable'
import { alpha, useTheme } from '@mui/material'
import Box from '@mui/material/Box'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useThemeMode } from '../../../theme/ThemeModeContext'
import { changeMediumWidgetsList } from '../DashboardSlice'
import { DrugStrip } from '../DrugStrip'
import { useGetMediumWidgets } from '../hooks'
import { MediumWidgetItem, MediumWidgetsName } from '../types'

import { SortableWidget } from './SortableWidget'

export const MediumWidgetsSection: FC = () => {
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const dragHoverColor =
    resolvedMode === 'dark' ? alpha(palette.background.paper, 0.3) : palette.blue[400]

  const dispatch = useAppDispatch()

  const widgetsMap = useGetMediumWidgets()
  const { customize, mediumWidgetsList } = useAppSelector(({ dashboard }) => dashboard)

  const initWidgetsOrder = Object.keys(mediumWidgetsList)
    .filter((item) => mediumWidgetsList[item as MediumWidgetsName])
    .map((widgetName) => ({
      id: widgetName,
    }))

  const [widgets, setWidgets] = useState<MediumWidgetItem[]>(initWidgetsOrder as MediumWidgetItem[])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
  )

  const widgetIds = useMemo(() => widgets.map((widget) => widget.id), [widgets])

  const handleOrderChange = (updatedWidgets: MediumWidgetItem[]) => {
    setWidgets(updatedWidgets)
    const widgetsNames = updatedWidgets.reduce((acc, mediumWidget) => {
      acc[mediumWidget.id] = mediumWidgetsList[mediumWidget.id]
      return acc
    }, {} as Record<keyof typeof MediumWidgetsName, boolean>)
    Object.keys(MediumWidgetsName).forEach((item) => {
      if (!(item in widgetsNames)) {
        widgetsNames[item as MediumWidgetsName] = mediumWidgetsList[item as MediumWidgetsName]
      }
    })

    dispatch(changeMediumWidgetsList(widgetsNames))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (!customize) {
      return
    }

    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = widgets.findIndex((widget) => widget.id === active.id)
    const newIndex = widgets.findIndex((widget) => widget.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    handleOrderChange(arrayMove(widgets, oldIndex, newIndex))
  }

  useEffect(() => {
    if (!customize) {
      const onlyVisible = Object.keys(mediumWidgetsList)
        .filter((item) => mediumWidgetsList[item as MediumWidgetsName])
        .map((widgetName) => ({
          id: widgetName,
        }))
      setWidgets(onlyVisible as MediumWidgetItem[])
    } else {
      const fullList = Object.keys(mediumWidgetsList).map((widgetName) => ({
        id: widgetName,
      }))
      setWidgets(fullList as MediumWidgetItem[])
    }
  }, [mediumWidgetsList, customize])

  return (
    <Box
      sx={{
        '&:hover': {
          backgroundColor: customize ? dragHoverColor : 'unset',
          transition: '0.4s all',
        },
        borderRadius: spacing(1),
        padding: customize ? spacing(1) : 'unset',
      }}
    >
      {customize && <DrugStrip />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${spacing(33.33)}, 1fr))`,
              gridAutoRows: '1fr',
              gap: spacing(2),
            }}
          >
            {widgets.map((widget) => (
              <SortableWidget key={widget.id} id={widget.id} disabled={!customize} sx={{ flex: 1 }}>
                {widgetsMap[widget.id as keyof typeof widgetsMap]}
              </SortableWidget>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}
